import { memo, useMemo } from "react"
import { Container, Heading, Flex, Text, Box } from "@chakra-ui/react"
import { useSection } from "@app/hooks/useSection"
import { CustomButton } from "@app/components/Button"
import { useContent } from "@app/hooks/useContent"
import { CustomImage } from "@app/components/Image"
import { Swiper, SwiperSlide } from "swiper/react"
import { useMedia } from "@app/hooks/useMedia"
import { useCore } from "@app/hooks/useCore"

type BadgeListProps = {
  badges: any
  badgeBgColor: any
  showMobileCarousel: boolean
  location: any
}

type BadgeProps = {
  badge: GatsbyTypes.SanityBadge
  badgeBgColor: any
}

const Badge = memo(({ badge, badgeBgColor }: BadgeProps) => {
  const isLink = badge.link?.link
  const href = isLink ? { href: badge.link.link } : {}
  // const { getResponsiveImage } = useImage()
  // const responsiveImg = getResponsiveImage({ _type: "imageResponsive", desktop: badge?.image })

  const style = useMemo(() => badge?.style || "default", [badge])

  return (
    <>
      {style === "default" ? (
        <Box
          as={isLink ? "a" : "div"}
          {...href}
          key={badge?._key}
          w={["110px", "157px"]}
          minH={["152px", "unset"]}
          h="full"
          bgColor={badgeBgColor}
          pt={[4, 9]}
          borderRadius="100px"
          display={["flex", "unset"]}
          flexDirection={["column", "unset"]}
          alignItems={["center", "unset"]}
          justifyContent={["flex-start", "auto"]}
          transition="transform 0.25s"
          _hover={{ transform: isLink && "scale(1.015)", textColor: isLink && "brand.green30" }}
        >
          <CustomImage className="no-globals" src={`${badge?.image?.asset?.url}?w=200`} width={[16, 21]} mx={[0, 9]} />
          <Text fontSize={["sm", "xl"]} pt={3} textAlign="center" pb={9} px={[1, 2]} textDecoration={isLink && "underline"}>
            {badge?.title}
          </Text>
        </Box>
      ) : (
        <Box
          as={isLink ? "a" : "div"}
          {...href}
          key={badge?._key}
          w={["40%", "157px"]}
          h="full"
          borderRadius="100px"
          overflow="hidden"
          display={["flex", "unset"]}
          flexDirection={["column", "unset"]}
          alignItems={["center", "unset"]}
          justifyContent={["flex-start", "auto"]}
          transition="transform 0.25s"
          _hover={{
            transform: isLink && "scale(1.015)",
            textColor: "brand.offWhite",
          }}
        >
          <CustomImage className="no-globals" src={`${badge?.image?.asset?.url}?w=200`} fit="cover" />
          <Text
            textColor="inherit"
            fontSize={["sm", "xl"]}
            pt={1}
            textAlign="center"
            pb={9}
            px={[1, 2]}
            textDecoration={isLink && "underline"}
          >
            {badge?.title}
          </Text>
        </Box>
      )}
    </>
  )
})

const BadgeList: React.FC<BadgeListProps> = ({ badges, badgeBgColor, showMobileCarousel = false }) => {
  const { isBase } = useMedia()

  if (showMobileCarousel && isBase) {
    return (
      <Swiper slidesPerView="auto">
        {badges?.map((badge: any) => (
          <SwiperSlide key={badge?._key} style={{ width: "unset" }}>
            <Box mx={4} h="full">
              <Badge badge={badge} badgeBgColor={badgeBgColor} />
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    )
  }

  return (
    <Flex direction="row" maxW={["600px", "1500px"]} mx="auto" justifyContent="center" wrap="wrap" columnGap={[8, 16]} rowGap={[6]}>
      {badges?.map((badge: any) => (
        <Badge badge={badge} key={badge?._key} badgeBgColor={badgeBgColor} />
      ))}
    </Flex>
  )
}

const Badges: React.FC<GatsbyTypes.SanitySectionBadges> = ({
  bgColor,
  title,
  textAboveBadges,
  textBelowBadges,
  button,
  badges,
  badgeBgColor,
  mobileBadgeLayout,
  analytics,
}) => {
  const {
    helpers: { isBrowser },
  } = useCore()
  const { handleTrackingClick } = useSection({ analytics })
  const { getContent } = useContent()
  const contentAboveBadges = getContent({ content: textAboveBadges })
  const contentBelowBadges = getContent({ content: textBelowBadges })

  const hasBadges = !!badges?.length
  const showMobileCarousel = mobileBadgeLayout === "carousel"

  const location = isBrowser && window.location
  const textAlign: any = ["left", "left", "center"]

  return (
    <Box bgColor={bgColor} py={[12, 12, 15]}>
      <Container px={showMobileCarousel ? 0 : ""}>
        {title && (
          <Heading as="h1" size={["h3", "h1"]} textAlign={textAlign} px={5} mb={[4, 4, 6]} fontSize={["5xl", "6xl"]}>
            {title}
          </Heading>
        )}
        {contentAboveBadges && (
          <Box
            maxW={97}
            mx="auto"
            mt={[4, 4, 6]}
            mb={[6, 6, 10]}
            textAlign={textAlign}
            px={showMobileCarousel ? [5, "unset"] : ""}
            sx={{ "& p": { fontSize: "md", lineHeight: "21px" } }}
          >
            {contentAboveBadges}
          </Box>
        )}
        {hasBadges && location && (
          <BadgeList badges={badges} badgeBgColor={badgeBgColor} showMobileCarousel={showMobileCarousel} location={location} />
        )}
        {contentBelowBadges && (
          <Box maxW={97} mx="auto" textAlign={textAlign} mt={[12, 12, 20]} mb={6} px={showMobileCarousel ? [5, "unset"] : ""}>
            {contentBelowBadges}
          </Box>
        )}
        <CustomButton
          button={button}
          center
          onClick={handleTrackingClick}
          width={["full", "unset"]}
          linkStyles={{
            px: showMobileCarousel ? [5, "unset"] : "",
            pt: 10,
          }}
        />
      </Container>
    </Box>
  )
}

export default memo(Badges)
